/**
 * JAISIU-3652 — global search deep-batch helper.
 *
 * Extends the global search modal (Cmd/Ctrl+K) so it can find matches
 * that live ONLY in older, non-rendered message batches. The gateway
 * endpoint `conversations.search` already walks the on-disk store on
 * the server side, but operators have reported that some matches only
 * surface after we load the corresponding chat locally — i.e. the
 * server's index can be slightly stale (recent activity between
 * writes) or a chat's older pages can be heavier than the gateway's
 * recency weight. This module fills that gap client-side without ever
 * forcing a permanent re-render of the older batches.
 *
 * Design:
 *   1. For every session listed in `sessionsResult.sessions` that has
 *      not yet been loaded into the live `chatMessagesBySession` cache
 *      (or is loaded but its loaded slice does not contain a match for
 *      the active query), issue a quiet `chat.history` RPC.
 *   2. Walk the `before=<oldestMessageId>` cursor until the server
 *      reports `hasMoreOlder: false` OR we hit a per-query page cap.
 *   3. Build an in-memory, EPHEMERAL text index per session: each
 *      message's `extractText` blob, lowercased. This blob is held
 *      in module-scope state keyed by session key, and is DISCARDED
 *      on modal close (see `clearDeepBatchSearchState`) so a search
 *      never leaks across modal opens.
 *   4. Scan the blob for the query. For every hit, produce a
 *      `GlobalSearchResult` row that matches the gateway wire shape
 *      so the existing `groupSearchResultsBySession` pipeline can
 *      merge them with the server's results without a different
 *      render path.
 *
 * Critical invariant (acceptance criterion #2): we NEVER write to
 * `chatMessagesBySession` from this path. The data lives only in
 * the ephemeral index; the live transcript (and therefore the
 * rendered chat thread) is untouched.
 *
 * Critical invariant (acceptance criterion #1): sessions whose only
 * matches live in batches older than what the chat thread has ever
 * loaded DO produce results. The deep walker visits every page the
 * server will return up to `MAX_PAGES_PER_SESSION`.
 */

import type { GlobalSearchResult } from "../controllers/global-search.ts";
import type { GatewaySessionRow } from "../types.ts";
import { extractText } from "./message-extract.ts";

/** Maximum older pages walked per session per query. Caps fanout. */
export const MAX_PAGES_PER_SESSION = 4;

/** Page size requested from `chat.history`. Matches the loader used by
 * `loadOlderChatHistory` so the server's cursor semantics align. */
export const DEEP_BATCH_PAGE_SIZE = 200;

/** Hard cap on parallel session fetches so a single search never
 * queues a hundred concurrent `chat.history` calls. */
export const DEEP_BATCH_CONCURRENCY = 4;

/** Hard cap on rows produced by the deep walker across ALL sessions.
 * Matches the gateway's `maxResults` so the merged set stays bounded. */
export const DEEP_BATCH_MAX_RESULTS = 50;

/** Minimum query length, mirrored from `global-search-modal.ts`. */
export const MIN_QUERY_LENGTH = 2;

export type DeepBatchGateway = {
  request<T = unknown>(method: string, params?: Record<string, unknown>): Promise<T>;
};

export type DeepBatchSessionSource = {
  /** All known session rows (matches the chat-history sidebar's view). */
  listSessions(): readonly GatewaySessionRow[];
  /** Live per-session message cache. Read-only — we never mutate it. */
  getMessagesBySession(): ReadonlyMap<string, unknown[]>;
  /** True when the gateway is connected. */
  isConnected(): boolean;
  /** The gateway client used to fire `chat.history` RPCs. */
  getClient(): DeepBatchGateway | null;
};

type ChatHistoryMessage = {
  id?: unknown;
  messageId?: unknown;
  transcriptId?: unknown;
  role?: unknown;
  content?: unknown;
  text?: unknown;
  createdAt?: unknown;
  timestamp?: unknown;
};

type ChatHistoryResponse = {
  messages?: unknown[];
  oldestMessageId?: string;
  hasMoreOlder?: boolean;
};

/**
 * One page from a single `chat.history` call. Stored in module scope so
 * a subsequent search in the same modal session can re-use the
 * already-fetched pages (and so the modal's "close" cleanup can free
 * them in one shot).
 */
type DeepBatchPage = {
  /** Server-issued id of the oldest message in this page. */
  oldestMessageId: string | null;
  /** True when the server says there is more to walk. */
  hasMoreOlder: boolean;
  /** Lowercased, newline-joined text blob of every message in the page. */
  blob: string;
  /**
   * Per-message text + offset, used to compute the matchedText window
   * (a 50-char context around the match) without re-running extractText
   * on render.
   */
  rows: DeepBatchMessageRow[];
};

type DeepBatchMessageRow = {
  /** Stable id (server-issued). */
  id: string;
  /** Role: user / assistant / system. */
  role: "user" | "assistant" | "system" | "other";
  /** Message createdAt in ms (best-effort). */
  timestamp?: number;
  /** Extracted text, may be empty for tool-only rows. */
  text: string;
  /** Position in the joined blob where this row's text starts. */
  blobStart: number;
};

type DeepBatchCacheEntry = {
  sessionKey: string;
  sessionLabel: string;
  pages: DeepBatchPage[];
  /** True when we have walked every older page the server can return. */
  exhausted: boolean;
};

// ───── module-scope ephemeral state ─────
//
// One map per process, one entry per session-key ever touched during
// the current modal-open. `clearDeepBatchSearchState` drops the entire
// map so a closed modal never leaks its index into the next open.

const deepBatchCache = new Map<string, DeepBatchCacheEntry>();

/** Monotonic counter incremented per `searchSessionsDeepBatch` call.
 * Used by the walker to bail out when a newer search supersedes an
 * older one (mirrors the generation-counter pattern in
 * `app-chat-cursor-parity.ts`). */
let deepBatchSearchGeneration = 0;

/** Active in-flight walker, if any. Lets `cancelDeepBatchSearch`
 * short-circuit the run rather than firing useless RPCs. */
let deepBatchInFlight = false;

/** Extract a stable id from a chat-history message, mirroring
 * `getMessageId` in `app-chat-cursor-parity.ts` but kept local so the
 * deep-batch walker has no dependency on the chat controller. */
function getMessageId(message: unknown): string | undefined {
  if (!message || typeof message !== "object") return undefined;
  const record = message as { id?: unknown; transcriptId?: unknown; messageId?: unknown };
  for (const key of ["id", "transcriptId", "messageId"] as const) {
    const value = record[key];
    if (typeof value === "string" && value.trim()) {
      return value.trim();
    }
  }
  const inner = (record as { message?: { id?: unknown } }).message;
  if (inner && typeof inner === "object") {
    const innerId = (inner as { id?: unknown }).id;
    if (typeof innerId === "string" && innerId.trim()) {
      return innerId.trim();
    }
  }
  return undefined;
}

/** Extract role from a chat-history message, normalised to the
 * wire shape. Anything unrecognised collapses to "other" so the
 * modal can still render it without crashing. */
function getRole(message: ChatHistoryMessage): "user" | "assistant" | "system" | "other" {
  const role = typeof message.role === "string" ? message.role : "";
  if (role === "user" || role === "assistant" || role === "system") {
    return role;
  }
  return "other";
}

/** Extract a millisecond timestamp from a chat-history message.
 * Falls back to undefined so the modal's "relative time" label simply
 * renders as empty (the same fallback the gateway uses). */
function getTimestamp(message: ChatHistoryMessage): number | undefined {
  for (const key of ["createdAt", "timestamp"] as const) {
    const raw = message[key];
    if (typeof raw === "number" && Number.isFinite(raw)) {
      return raw;
    }
    if (typeof raw === "string") {
      const parsed = Date.parse(raw);
      if (!Number.isNaN(parsed)) return parsed;
    }
  }
  return undefined;
}

/** Slice of one chat-history message: a thin record used to build
 * the lowercased index blob AND to emit a `GlobalSearchResult` row
 * when the blob matches. */
function extractRow(message: unknown): { id: string; row: DeepBatchMessageRow } | null {
  if (!message || typeof message !== "object") return null;
  const id = getMessageId(message);
  if (!id) return null;
  const m = message as ChatHistoryMessage;
  const text = extractText(message) ?? "";
  return {
    id,
    row: {
      id,
      role: getRole(m),
      timestamp: getTimestamp(m),
      text,
      blobStart: -1, // filled in when the blob is built
    },
  };
}

/** Build one `DeepBatchPage` from a `chat.history` response. */
function buildPage(response: ChatHistoryResponse): DeepBatchPage {
  const messages = Array.isArray(response.messages) ? response.messages : [];
  const parts: string[] = [];
  const rows: DeepBatchMessageRow[] = [];
  for (const message of messages) {
    const out = extractRow(message);
    if (!out) continue;
    const text = out.row.text;
    if (!text) continue;
    out.row.blobStart = parts.join("\n").length;
    parts.push(text);
    rows.push(out.row);
  }
  const oldestMessageId =
    typeof response.oldestMessageId === "string" && response.oldestMessageId
      ? response.oldestMessageId
      : null;
  return {
    oldestMessageId,
    hasMoreOlder: response.hasMoreOlder === true,
    blob: parts.join("\n").toLowerCase(),
    rows,
  };
}

/** Build a `DeepBatchPage` from the already-loaded live cache for a
 * session, so a query that lands a hit in the operator's currently
 * open chat thread does not need a redundant `chat.history` round
 * trip. The live cache row also gets folded into the per-session
 * ephemeral blob. */
function buildPageFromLoaded(messages: readonly unknown[]): DeepBatchPage | null {
  if (messages.length === 0) return null;
  const parts: string[] = [];
  const rows: DeepBatchMessageRow[] = [];
  for (const message of messages) {
    const out = extractRow(message);
    if (!out) continue;
    const text = out.row.text;
    if (!text) continue;
    out.row.blobStart = parts.join("\n").length;
    parts.push(text);
    rows.push(out.row);
  }
  if (rows.length === 0) return null;
  return {
    oldestMessageId: rows[0]?.id ?? null,
    // We don't know `hasMoreOlder` from the live cache alone; the
    // walker will issue a real `chat.history` to learn it. The page
    // is still useful as a quick-match accelerator.
    hasMoreOlder: true,
    blob: parts.join("\n").toLowerCase(),
    rows,
  };
}

/** Best-effort human label for a session row, mirroring
 * `sessionLabel` in `views/sessions-delete-confirm.ts`. */
function labelFor(row: GatewaySessionRow): string {
  if (row.label) return row.label;
  if (row.displayName) return row.displayName;
  if (row.subject) return row.subject;
  if (row.derivedTitle) return row.derivedTitle;
  return row.key;
}

/** Compute a 50-char context window around the first match, with
 * "…" prefixes. Mirrors the gateway's `matchedText` field. */
function buildMatchedText(text: string, query: string, maxLen = 50): string {
  if (!text) return "";
  const idx = text.toLowerCase().indexOf(query.toLowerCase());
  if (idx < 0) return text.length > maxLen ? `…${text.slice(0, maxLen - 1)}` : text;
  const half = Math.floor((maxLen - 1) / 2);
  const start = Math.max(0, idx - half);
  const end = Math.min(text.length, start + maxLen);
  const prefix = start > 0 ? "…" : "";
  const suffix = end < text.length ? "…" : "";
  return prefix + text.slice(start, end) + suffix;
}

/** True if the row's text contains the query (case-insensitive).
 * Exported for unit tests. */
export function rowMatchesQuery(row: DeepBatchMessageRow, query: string): boolean {
  if (!row.text) return false;
  return row.text.toLowerCase().includes(query.toLowerCase());
}

/** Scan a single page's blob for matches. Returns a row per match
 * (a page can contain multiple matches in one message — the gateway
 * emits one row per match too). Bounded by `limit` so a page full of
 * hits does not blow the result cap. */
function scanPageForMatches(
  page: DeepBatchPage,
  query: string,
  limit: number,
): GlobalSearchResult[] {
  if (limit <= 0) return [];
  const out: GlobalSearchResult[] = [];
  const q = query.toLowerCase();
  for (const row of page.rows) {
    if (out.length >= limit) break;
    if (!row.text) continue;
    const haystack = row.text.toLowerCase();
    let cursor = 0;
    while (cursor < haystack.length) {
      const idx = haystack.indexOf(q, cursor);
      if (idx < 0) break;
      out.push({
        sessionKey: "", // filled by caller
        sessionLabel: "", // filled by caller
        messageIndex: -1, // filled by caller
        role: row.role === "other" ? "system" : row.role,
        text: row.text.length > 200 ? row.text.slice(0, 200) : row.text,
        matchedText: buildMatchedText(row.text, query),
        timestamp: row.timestamp,
      });
      cursor = idx + q.length;
      if (out.length >= limit) break;
    }
  }
  return out;
}

/** Walk one session end-to-end: read the live cache, then page
 * through `chat.history` until exhausted or `MAX_PAGES_PER_SESSION`.
 * Returns matches up to `limit`. */
async function walkSession(
  source: DeepBatchSessionSource,
  sessionKey: string,
  sessionLabel: string,
  query: string,
  limit: number,
): Promise<{ results: GlobalSearchResult[]; remainingLimit: number; appendedPages: number }> {
  if (limit <= 0) return { results: [], remainingLimit: 0, appendedPages: 0 };
  const client = source.getClient();
  if (!client) return { results: [], remainingLimit: limit, appendedPages: 0 };

  let appendedPages = 0;
  const entry: DeepBatchCacheEntry = deepBatchCache.get(sessionKey) ?? {
    sessionKey,
    sessionLabel,
    pages: [],
    exhausted: false,
  };
  if (entry.sessionLabel !== sessionLabel) {
    // Sessions can be renamed; keep the freshest label so the modal
    // shows the current name. The pages themselves are unaffected.
    entry.sessionLabel = sessionLabel;
  }
  if (deepBatchCache.get(sessionKey) !== entry) {
    deepBatchCache.set(sessionKey, entry);
  }

  // Seed the per-session index with the live cache (if any) on the
  // first visit. We DO NOT mutate the live cache — we just read.
  if (entry.pages.length === 0) {
    const live = source.getMessagesBySession().get(sessionKey);
    if (live && live.length > 0) {
      const seed = buildPageFromLoaded(live);
      if (seed) {
        entry.pages.push(seed);
        appendedPages += 1;
      }
    }
  }

  // Scan any pages we already have for matches first; only fetch
  // older pages if the existing pages did not saturate the limit.
  let localResults: GlobalSearchResult[] = [];
  for (const page of entry.pages) {
    if (localResults.length >= limit) break;
    localResults = localResults.concat(
      scanPageForMatches(page, query, limit - localResults.length),
    );
  }

  // If the live cache (the slice the operator is already looking at)
  // produced ANY match, do not page older. The live cache is the
  // most-recent slice; if it contains a hit the operator is already
  // in the right place. Walking older would only widen the result
  // set with strictly-older context the modal already shows via the
  // gateway path. Save the bandwidth.
  if (entry.pages.length > 0 && localResults.length > 0) {
    return { results: localResults, remainingLimit: limit - localResults.length, appendedPages };
  }

  if (localResults.length >= limit || entry.exhausted) {
    return { results: localResults, remainingLimit: limit - localResults.length, appendedPages };
  }

  // Walk older pages. The cursor is the OLDEST id we have loaded so
  // far. If we have no live cache at all, the very first call uses
  // the default latest-N window (no `before`).
  let cursor: string | null = null;
  const firstPage = entry.pages[0];
  if (firstPage && firstPage.oldestMessageId) {
    cursor = firstPage.oldestMessageId;
  } else {
    // Live cache had no id we trust; start from the default window.
    cursor = null;
  }

  for (let page = 0; page < MAX_PAGES_PER_SESSION; page += 1) {
    if (localResults.length >= limit) break;
    const params: Record<string, unknown> = {
      sessionKey,
      limit: DEEP_BATCH_PAGE_SIZE,
    };
    if (cursor) {
      params.before = cursor;
    }
    let response: ChatHistoryResponse;
    try {
      response = (await client.request<ChatHistoryResponse>("chat.history", params)) ?? {};
    } catch {
      // Best-effort: a single failure must not abort the rest of the
      // walker. Mark exhausted so we don't retry on the next search
      // and move on.
      entry.exhausted = true;
      break;
    }
    const built = buildPage(response);
    entry.pages.push(built);
    appendedPages += 1;
    if (built.oldestMessageId) cursor = built.oldestMessageId;
    entry.exhausted = !built.hasMoreOlder;
    if (built.rows.length === 0) {
      // Server returned an empty page (cursor was stale, or the
      // session is brand-new with no older history). Stop walking.
      break;
    }
    if (localResults.length < limit) {
      localResults = localResults.concat(
        scanPageForMatches(built, query, limit - localResults.length),
      );
    }
    if (!built.hasMoreOlder) break;
  }

  return { results: localResults, remainingLimit: limit - localResults.length, appendedPages };
}

/**
 * Run a deep-batch search across every session known to the
 * `sessionsResult`. Returns results shaped like the gateway's
 * `GlobalSearchResult[]` so the caller can merge them with the
 * server's response.
 *
 * Concurrency is bounded by `DEEP_BATCH_CONCURRENCY`. The walker
 * skips sessions already in the live cache IF their loaded slice
 * already produced a saturated match set (i.e. we don't refetch
 * sessions whose loaded slice alone covered the cap).
 */
export async function searchSessionsDeepBatch(
  source: DeepBatchSessionSource,
  query: string,
  options: { maxResults?: number } = {},
): Promise<GlobalSearchResult[]> {
  const maxResults = options.maxResults ?? DEEP_BATCH_MAX_RESULTS;
  const trimmed = query.trim();
  if (trimmed.length < MIN_QUERY_LENGTH) return [];
  if (!source.isConnected()) return [];
  const sessions = source.listSessions();
  if (sessions.length === 0) return [];

  // Bump the generation. If a previous walker is still running, the
  // bump alone does not stop it; cancelDeepBatchSearch does. The
  // generation is mainly for the test harness — the walker also
  // checks the `deepBatchInFlight` flag below.
  const generation = (deepBatchSearchGeneration += 1);
  if (deepBatchInFlight) {
    // Only one deep search at a time. Bail so we don't queue.
    return [];
  }
  deepBatchInFlight = true;

  try {
    if (generation !== deepBatchSearchGeneration) {
      return [];
    }

    // Skip the active chat: its live cache is already consulted by
    // the shallow search, and the operator is reading it right now
    // so a redundant load would be wasted bandwidth. We do NOT skip
    // sessions already in the cache for the same reason — the live
    // cache seed inside `walkSession` does the right thing without
    // a round trip.
    //
    // Actually we DO want to walk every session: a session in the
    // cache may still have older pages that were never loaded, and
    // matches there are exactly the deep-batch problem we are
    // solving. The only sessions we can skip are ones we have
    // ALREADY walked to exhaustion AND whose loaded slice is the
    // full transcript — and we do not know that with certainty, so
    // we walk everything and let the walker be a no-op via the
    // `exhausted` flag.
    //
    // Filter to sessions the sidebar/operator could plausibly care
    // about: drop rows with an empty key.
    const candidates = sessions.filter((s) => typeof s.key === "string" && s.key.trim());

    const results: GlobalSearchResult[] = [];
    let messageIndexCursor = 0;
    let remaining = maxResults;
    for (let i = 0; i < candidates.length; i += DEEP_BATCH_CONCURRENCY) {
      if (remaining <= 0) break;
      if (generation !== deepBatchSearchGeneration) break;
      const slice = candidates.slice(i, i + DEEP_BATCH_CONCURRENCY);
      const perSession = await Promise.all(
        slice.map(async (row) => {
          const label = labelFor(row);
          const { results: sessionResults } = await walkSession(
            source,
            row.key,
            label,
            trimmed,
            remaining,
          );
          return { sessionKey: row.key, sessionLabel: label, sessionResults };
        }),
      );
      for (const { sessionKey, sessionLabel, sessionResults } of perSession) {
        for (const r of sessionResults) {
          if (remaining <= 0) break;
          messageIndexCursor += 1;
          results.push({
            ...r,
            sessionKey,
            sessionLabel,
            messageIndex: messageIndexCursor,
          });
          remaining -= 1;
        }
        if (remaining <= 0) break;
      }
    }
    return results;
  } finally {
    if (deepBatchSearchGeneration === generation) {
      deepBatchInFlight = false;
    }
  }
}

/**
 * Clear every cached page the deep walker accumulated. Called on
 * modal close so the next open starts cold. Exposed for tests.
 */
export function clearDeepBatchSearchState(): void {
  deepBatchCache.clear();
  deepBatchSearchGeneration += 1;
  deepBatchInFlight = false;
}

/** Drop the cached pages for a single session. Used by tests and by
 * chat-switch paths that need to forget the ephemeral index when the
 * operator permanently closes a session. */
export function dropDeepBatchSearchState(sessionKey: string): void {
  deepBatchCache.delete(sessionKey);
}

/** Snapshot the ephemeral cache for test assertions. Read-only. */
export function inspectDeepBatchCache(): ReadonlyMap<string, DeepBatchCacheEntry> {
  return deepBatchCache;
}

/**
 * INTERNAL — test seam for unit tests that need to prime the
 * deep-batch cache directly without running the async walker.
 * Not exported in the package barrel; consumers should reach for
 * `searchSessionsDeepBatch` in production code. Exposed here so
 * the JAISIU-3653 jump-to-match helpers can be tested
 * deterministically: a `searchSessionsDeepBatch` round-trip
 * requires a fake gateway client, which is overkill for a pure
 * lookup test.
 */
export function __testPrimeDeepBatchCache(
  sessionKey: string,
  entry: DeepBatchCacheEntry,
): void {
  deepBatchCache.set(sessionKey, entry);
}

/** True if a deep-batch search is currently walking the gateway. */
export function isDeepBatchSearchInFlight(): boolean {
  return deepBatchInFlight;
}
